import { UserMenuAlignEnum, UserMenuConfig, UserMenuTypeEnum } from './UserMenu/UserMenu';
import { ROOT_URL } from '../../../../constants/endpoints';
import { getCurrentLanguage } from '../../../../../helpers/LangHelper';
import { hasPermission, IPermissionsProps, isVisibleByBranding } from '../../../../../helpers/PermissionsHelper';
import { RoutePermissions } from '../../../../../constants/RoutePermissions';
import { DefaultRoutePermissions } from '../../../../../constants/DefaultRoutePermissions';
import { BrandingHiddenSections } from '../../../../../main/constants/BrandingHiddenSections';
import { SUBSCRIPTION_BILLING_URL, SETTINGS_UPGRADE_URL, MENU_LINKS } from '../../../../../config/routes';

export interface ITopMenuConfigurationProps extends IPermissionsProps {
    hiddenSections?: BrandingHiddenSections[];
}

export enum UserItemMenuSectionsName {
    SUBSCRIPTION = 'default.menus._top.subscription',
    MY_ACCOUNT = 'default.menus._top.my_account',
    SECURITY = 'default.menus._top.security',
    BRANDING = 'default.menus._top.branding',
    INTEGRATIONS = 'default.menus._top.integrations',
    ACCOUNT_SETTINGS = 'default.menus._top.account_settings',
    UPGRADE = 'default.menus._top.upgrade',
    HELP = 'default.menus._top.help',
    CLOSE_SESSION = 'default.menus._top.close_session',
    GAUGES = 'gauges',
}

export const userMenuConfig = (
    topMenuConfigurationProps: ITopMenuConfigurationProps,
    sectionsWithTag: Array<string> = []
): UserMenuConfig[] => {

    let menuConfig: UserMenuConfig[] = [];

    if (topMenuConfigurationProps.permissions?.includes(RoutePermissions.ShowNewSettings)) {
        menuConfig = [
            {
                link: MENU_LINKS.ACCOUNT_SETTINGS.BASE,
                name: UserItemMenuSectionsName.ACCOUNT_SETTINGS,
                withDivider: false,
                type: UserMenuTypeEnum.ANCHOR,
                align: UserMenuAlignEnum.NONE,
                visible: hasPermission(topMenuConfigurationProps, DefaultRoutePermissions.Account),
            },
            {
                link: SUBSCRIPTION_BILLING_URL,
                name: UserItemMenuSectionsName.SUBSCRIPTION,
                withDivider: true,
                type: UserMenuTypeEnum.ANCHOR,
                align: UserMenuAlignEnum.NONE,
                visible: (
                    hasPermission(topMenuConfigurationProps, RoutePermissions.IsAdmin) ||
                    hasPermission(topMenuConfigurationProps, RoutePermissions.SubscriptionSection)
                ) && isVisibleByBranding(topMenuConfigurationProps.hiddenSections, BrandingHiddenSections.Billing),
            },
            {
                link: undefined,
                name: UserItemMenuSectionsName.GAUGES,
                withDivider: true,
                type: UserMenuTypeEnum.GAUGES,
                align: UserMenuAlignEnum.NONE,
                classes: 'user-menu-component__menu-section-gauges',
                visible: hasPermission(topMenuConfigurationProps, RoutePermissions.ShowGauges),
            },
            {
                link: SETTINGS_UPGRADE_URL,
                name: UserItemMenuSectionsName.UPGRADE,
                withDivider: true,
                type: UserMenuTypeEnum.BUTTON,
                align: UserMenuAlignEnum.CENTER,
                visible:
                    hasPermission(topMenuConfigurationProps, RoutePermissions.IsAdmin) &&
                    isVisibleByBranding(topMenuConfigurationProps.hiddenSections, BrandingHiddenSections.Upgrade),
            },
            {
                link: 'https://help.signaturit.com',
                name: UserItemMenuSectionsName.HELP,
                withDivider: true,
                newTab: true,
                classes: 'user-menu-component__menu-section-help',
                type: UserMenuTypeEnum.ANCHOR,
                align: UserMenuAlignEnum.NONE,
                visible: true,
            },
            {
                link: `${ROOT_URL}/${getCurrentLanguage()}/logout`,
                name: UserItemMenuSectionsName.CLOSE_SESSION,
                withDivider: true,
                type: UserMenuTypeEnum.ANCHOR,
                align: UserMenuAlignEnum.NONE,
                icon: 'icon-close-session',
                visible: true,
            },
        ];
    }

    if (topMenuConfigurationProps.permissions?.includes(RoutePermissions.ShowLegacySettings)) {
        menuConfig = [
            {
                link: MENU_LINKS.SETTINGS.ACCOUNT.LINK,
                name: UserItemMenuSectionsName.MY_ACCOUNT,
                withDivider: false,
                type: UserMenuTypeEnum.ANCHOR,
                align: UserMenuAlignEnum.NONE,
                visible: hasPermission(topMenuConfigurationProps, DefaultRoutePermissions.Account),
            },
            {
                link: MENU_LINKS.SETTINGS.SECURITY.LINK,
                name: UserItemMenuSectionsName.SECURITY,
                withDivider: false,
                type: UserMenuTypeEnum.ANCHOR,
                align: UserMenuAlignEnum.NONE,
                visible: hasPermission(topMenuConfigurationProps, DefaultRoutePermissions.Security),
            },
            {
                link: MENU_LINKS.SETTINGS.BRANDING.LINK,
                name: UserItemMenuSectionsName.BRANDING,
                withDivider: false,
                type: UserMenuTypeEnum.ANCHOR,
                align: UserMenuAlignEnum.NONE,
                visible: hasPermission(topMenuConfigurationProps, DefaultRoutePermissions.Branding),
            },
            {
                link: MENU_LINKS.SETTINGS.INTEGRATIONS.LINK,
                name: UserItemMenuSectionsName.INTEGRATIONS,
                withDivider: false,
                type: UserMenuTypeEnum.ANCHOR,
                align: UserMenuAlignEnum.NONE,
                visible:
                    hasPermission(topMenuConfigurationProps, DefaultRoutePermissions.Integrations) &&
                    isVisibleByBranding(topMenuConfigurationProps.hiddenSections, BrandingHiddenSections.Integrations),
            },
            {
                link: SUBSCRIPTION_BILLING_URL,
                name: UserItemMenuSectionsName.SUBSCRIPTION,
                withDivider: true,
                type: UserMenuTypeEnum.ANCHOR,
                align: UserMenuAlignEnum.NONE,
                visible:
                    hasPermission(topMenuConfigurationProps, RoutePermissions.SubscriptionSection) &&
                    isVisibleByBranding(topMenuConfigurationProps.hiddenSections, BrandingHiddenSections.Billing),
            },
            {
                link: undefined,
                name: UserItemMenuSectionsName.GAUGES,
                withDivider: true,
                type: UserMenuTypeEnum.GAUGES,
                align: UserMenuAlignEnum.NONE,
                classes: 'user-menu-component__menu-section-gauges',
                visible: hasPermission(topMenuConfigurationProps, RoutePermissions.ShowGauges),
            },
            {
                link: MENU_LINKS.SETTINGS.BILLING.LINK,
                name: UserItemMenuSectionsName.UPGRADE,
                withDivider: true,
                type: UserMenuTypeEnum.BUTTON,
                align: UserMenuAlignEnum.CENTER,
                visible:
                    hasPermission(topMenuConfigurationProps, RoutePermissions.IsAdmin) &&
                    isVisibleByBranding(topMenuConfigurationProps.hiddenSections, BrandingHiddenSections.Upgrade),
            },
            {
                link: 'https://help.signaturit.com',
                name: UserItemMenuSectionsName.HELP,
                withDivider: true,
                newTab: true,
                classes: 'user-menu-component__menu-section-help',
                type: UserMenuTypeEnum.ANCHOR,
                align: UserMenuAlignEnum.NONE,
                visible: true,
            },
            {
                link: `${ROOT_URL}/${getCurrentLanguage()}/logout`,
                name: UserItemMenuSectionsName.CLOSE_SESSION,
                withDivider: true,
                type: UserMenuTypeEnum.ANCHOR,
                align: UserMenuAlignEnum.NONE,
                icon: 'icon-close-session',
                visible: true,
            },
        ];
    }

    return menuConfig;

};
